import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_ONE_NETWORK_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableOneNetworkAppInput = {
  appType: AppTypeEnum
  orgName: string
  password: string
  serverPath: string
  username: string
}

const formSchema = z.object({
  orgName: z.string().min(1, 'Organization name is required'),
  password: z.string().min(1, 'Password is required'),
  serverPath: z.string().min(1, 'Server path is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the ATT Fleet Management app.
 */
export function OneNetworkModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      orgName: '',
      serverPath: '',
      password: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableOneNetworkApp, { loading }] = useMutation<
    {
      enableOneNetworkAppCredentials: {
        appType: string
        orgName: string
        serverPath: string
        password: string
        username: string
      }
    },
    EnableOneNetworkAppInput
  >(ENABLE_ONE_NETWORK_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('OneNetwork app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      let errorMessage = ''
      errorMessage =
        err.message === 'unauthorized'
          ? 'Please enter correct username or password.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableOneNetworkApp({
      variables: {
        appType: appType as AppTypeEnum,
        orgName: values.orgName,
        serverPath: values.serverPath,
        password: values.password,
        username: values.username
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable OneNetwork for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires an Admin OneNetwork account. Enter your admin username, password and
            server path.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your OneNetwork username" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your OneNetwork password" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="orgName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your OneNetwork organization name" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serverPath"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Open any shipment in OneNetwork and paste the URL here"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href={app.docs}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
